import axios, { type AxiosRequestConfig } from 'axios'
import { toast } from 'react-toastify'
import { logError } from '../utils/sentry'

export interface ApiRequestConfig extends AxiosRequestConfig {
  silentAuthCheck?: boolean
  skipAuthToast?: boolean
}

const baseURL = 'https://app.porygon.techtio.work/api'
// const baseURL = import.meta.env.VITE_BACKEND_HOST

const api = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

api.interceptors.request.use(
  (config) => config,
  (error) => {
    logError('API Request Error', error)
    console.error('API Request Error:', error)
    return Promise.reject(error)
  },
)

/**
 * Backend validation errors come as { message, data: { <field>: <message> | <message>[] } }.
 * Those field messages are the user-facing ones, so they win over the generic `message`.
 */
const extractFieldErrorMessages = (responseData: unknown): string[] => {
  if (!responseData || typeof responseData !== 'object') return []

  const fields = (responseData as { data?: unknown }).data
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return []

  return Object.values(fields as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
}

api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Response Error:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
    })

    logError('API Response Error', error)

    const cfg = (error.config ?? {}) as ApiRequestConfig
    const isSilentAuthCheck = Boolean(cfg.silentAuthCheck)
    const skipAuthToast = Boolean(cfg.skipAuthToast)

    const fieldErrorMessages = isSilentAuthCheck ? [] : extractFieldErrorMessages(error.response?.data)

    // A response carrying field errors is a rejected request, not an expired session:
    // show only those messages and skip the generic status/message fallbacks below.
    if (fieldErrorMessages.length > 0) {
      for (const message of fieldErrorMessages) {
        toast.error(message, { toastId: message })
      }

      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !isSilentAuthCheck && !skipAuthToast) {
      toast.error('Session expired. Please login again.', { toastId: 'auth-401' })
    }

    if (!isSilentAuthCheck) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else if (error.response?.status === 403) {
        toast.error('Access denied.')
      } else if (error.response?.status === 404) {
        toast.error('Resource not found.')
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.')
      }
    }

    return Promise.reject(error)
  },
)

export default api
